import { SectionShell, Panel, Note } from "@/components/dash/primitives";
import { StatCard } from "@/components/dash/StatCard";
import { ChartTip, CHART } from "@/components/dash/chartkit";
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine,
} from "recharts";

export default function Reliability({ data, num, title }) {
  const trend = data.trend_labels.map((l, i) => ({
    label: l, Headhaul: data.trend_headhaul[i], Backhaul: data.trend_backhaul[i],
  }));
  const idle = data.idle_labels.map((l, i) => ({ label: l, value: data.idle_values[i], ...((data.idle_meta || {})[l] || {}) }));

  const IdleTip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    const p = payload[0].payload;
    return (
      <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 shadow-sm">
        <div className="font-num text-[11px] uppercase tracking-wider text-slate-400">{label}</div>
        <div className="font-display text-sm font-semibold text-slate-900 mt-0.5">
          {p.value}%{p.ships ? ` · ${p.ships} ships / ${Math.round(p.teu / 1000)}k TEU` : ""}
        </div>
      </div>
    );
  };

  return (
    <SectionShell id="reliability" num={num} title={title}>
      <p className="text-slate-700 leading-relaxed max-w-4xl mb-10">{data.intro}</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {data.stats.map((s, i) => (
          <StatCard key={i} stat={s} testid={`reliability-stat-${i}`} />
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Panel
          title="Asia–Oceania reliability trend"
          subtitle="Last 8 rolling 2-month periods (%)"
          source="Source: Sea-Intelligence Global Liner Performance, LP181 (Sep 2026)"
          testid="chart-reliability-trend"
          csv={{ name: "Reliability trend", columns: ["Period", "Headhaul %", "Backhaul %"], rows: trend.map((d) => [d.label, d.Headhaul, d.Backhaul]) }}
        >
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={trend} margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke="#f1f5f9" />
              <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fill: "#64748b" }} />
              <YAxis domain={[40, 70]} tickFormatter={(v) => `${v}%`} tickLine={false} axisLine={false} tick={{ fill: "#64748b" }} width={44} />
              <Tooltip content={<ChartTip formatter={(v) => `${v}%`} />} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <ReferenceLine y={49.9} stroke={CHART.slate} strokeDasharray="4 4" label={{ value: "Global avg 49.9% (Aug)", position: "insideTopRight", fill: "#94a3b8", fontSize: 10 }} />
              <Line type="monotone" dataKey="Headhaul" stroke={CHART.blue} strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
              <Line type="monotone" dataKey="Backhaul" stroke={CHART.orange} strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </Panel>

        <Panel
          title="Carrier reliability, Asia ↔ Oceania"
          subtitle="Jul/Aug 2026 (major Asia↔Oceania carriers)"
          source="Source: Sea-Intelligence Global Liner Performance, LP181 (Sep 2026)"
          testid="chart-carrier-reliability"
          csv={{ name: "Carrier reliability", columns: ["Carrier", "Headhaul %", "Backhaul %", "Tracked arrivals"], rows: data.carriers.map((d) => [d.carrier, d.headhaul, d.backhaul, d.arrivals]) }}
        >
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data.carriers} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke="#f1f5f9" />
              <XAxis dataKey="carrier" tickLine={false} axisLine={false} tick={{ fill: "#64748b", fontSize: 10 }} interval={0} angle={-18} textAnchor="end" height={50} />
              <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} tickLine={false} axisLine={false} tick={{ fill: "#64748b" }} width={40} />
              <Tooltip cursor={{ fill: "#f8fafc" }} content={<ChartTip formatter={(v, p) => `${v}%${p?.arrivals ? ` · ${p.arrivals} arrivals` : ""}`} />} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="headhaul" name="Headhaul" fill={CHART.blue} radius={[3, 3, 0, 0]} maxBarSize={22} />
              <Bar dataKey="backhaul" name="Backhaul" fill={CHART.orange} radius={[3, 3, 0, 0]} maxBarSize={22} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      </div>
      <Note>{data.trend_note}</Note>
      <Note>{data.carrier_note}</Note>

      <div className="mt-8">
        <Panel
          title="Global idle containership fleet"
          subtitle="% of total cellular fleet — 11 May to 5 Oct 2026"
          source="Source: Alphaliner Newsletters 2026-31 to 2026-41"
          testid="chart-idle-fleet"
          csv={{ name: "Idle fleet", columns: ["Week", "Idle %"], rows: idle.map((d) => [d.label, d.value]) }}
        >
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={idle} margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
              <defs>
                <linearGradient id="idleGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={CHART.blue} stopOpacity={0.25} />
                  <stop offset="100%" stopColor={CHART.blue} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid vertical={false} stroke="#f1f5f9" />
              <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fill: "#64748b" }} />
              <YAxis domain={[0, 1]} tickFormatter={(v) => `${v}%`} tickLine={false} axisLine={false} tick={{ fill: "#64748b" }} width={40} />
              <Tooltip content={<IdleTip />} />
              <Area type="monotone" dataKey="value" name="Idle fleet" stroke={CHART.blue} strokeWidth={2.5} fill="url(#idleGrad)" dot={{ r: 3 }} activeDot={{ r: 5 }} />
            </AreaChart>
          </ResponsiveContainer>
        </Panel>
      </div>
      <Note>{data.idle_note}</Note>
    </SectionShell>
  );
}
